'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

import { SubAccountIdScope } from '@flipster/r230522/normal-sub/sub-account';
import type { TpSlDragIntent } from '@flipster/tvchart';

import { SymbolScope } from '../shared/symbol/bunja';
import { ParentIdScope, PositionTypeScope, positionTpslBunja } from '../shared/tpsl/bunja';
import { useTpslModalOverlay } from '../tpsl-modal/use-tpsl-modal-overlay';

interface Props {
  /** 마켓 심볼 (예: 'BTC'). normal-sub 포지션 조회 키와 동일해야 한다. */
  symbol: string;
  intent: TpSlDragIntent;
  /** 모달을 연 뒤 호출. 보통 pending intent를 비워 이 컴포넌트를 언마운트한다. */
  onDone: () => void;
}

/**
 * 차트 드래그(`onOpenTpSlModal`)로 도출된 intent를 받아, 해당 포지션의 TP/SL 모달을 드래그한
 * 트리거 가격으로 prefill해 연다 — 생성(orderId 없음)/수정(orderId 있음)은 intent로 분기.
 *
 * `useTpslModalOverlay`는 포지션별 scope(slot/marginType)를 훅 시점에 바인딩하므로,
 * intent가 생길 때만 이 headless 컴포넌트를 mount하여 그 포지션 scope로 1회 오픈한다.
 * slot/포지션이 아직 로드되지 않았으면 준비될 때까지 기다린다.
 */
export function ChartTpSlDragOpener({ symbol, intent, onDone }: Props) {
  const tpslType = intent.kind === 'TP' ? 'tp' : 'sl';

  const { slotAtom, openPositionAtom } = useBunja(positionTpslBunja, [
    SymbolScope.bind(symbol),
    PositionTypeScope.bind('open'),
    ParentIdScope.bind(intent.positionId),
    SubAccountIdScope.bind(undefined),
  ]);
  const slot = useAtomValue(slotAtom);
  const position = useAtomValue(openPositionAtom);

  const { openTpslModal } = useTpslModalOverlay(
    symbol,
    'open',
    tpslType,
    slot,
    position?.marginType,
    intent.positionId,
  );

  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current) return;
    // 포지션/slot이 준비되기 전엔 열지 않는다 (잘못된 scope로 열리는 것 방지).
    if (!position || slot < 0) return;

    openedRef.current = true;
    // intent.orderId가 있으면(기존 order line 드래그) 수정 모드로, 없으면 생성 모드로 연다.
    openTpslModal({ tpslType, initialTriggerPrice: intent.triggerPrice, modifyOrderId: intent.orderId });
    onDone();
  }, [position, slot, openTpslModal, tpslType, intent.triggerPrice, intent.orderId, onDone]);

  return null;
}
